/**
 * <NetworkCanvas />
 * ─────────────────────────────────────────────────────────────────────
 * Shared "Palantir-style" live background used on the splash page AND
 * the auth pages so the visual transition between marketing → sign-in
 * feels seamless. Two layers on a single full-viewport canvas:
 *
 *   • Layer 1 — three concentric orbital rings (faint, rotating at
 *     different rates) anchored at the right side of the page where
 *     marketing CTAs and the sign-in form both sit.
 *   • Layer 2 — drifting particles + connection links (knowledge-graph
 *     aesthetic) covering the whole viewport.
 *
 * Theme-aware (`isDark` prop):
 *   • Dark canvas — bright violet particles + violet rings + violet links
 *     (the original luxe-dark look).
 *   • Light canvas — warm-gold + soft-violet 4-tint mix at lower alphas,
 *     gold rings, warm-tan links. Reads as embossed letterhead, not a
 *     foreground graphic. (User feedback v161: "soft / gentle / original
 *     gold + purple hues / background only".)
 *
 * Single source of truth — extracted in v162 from the previous local
 * copy in EcosystemGateway. Login now imports the same component, so
 * splash → /login visually feels like one continuous canvas.
 */
import React, { useEffect, useRef } from 'react';

export default function NetworkCanvas({ isDark, testId = 'network-canvas' }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let w = window.innerWidth, h = window.innerHeight;
    const setSize = () => {
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * window.devicePixelRatio;
      canvas.height = h * window.devicePixelRatio;
      canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
    };
    setSize();
    window.addEventListener('resize', setSize);

    let paused = false;
    const onVis = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    // ═══════════════════════════════════════════════════════════════════
    // v270.2 — UNIFIED starfield in BOTH modes. Pure "drifting stars ·
    // parallax · wide, calm" — no orbital rings, no connection lines,
    // no aurora orbs. Palette adapts per theme:
    //   • Light — champagne gold family on warm cream
    //   • Dark  — violet family (with pale-violet highlights) on black
    // ═══════════════════════════════════════════════════════════════════
    const starTints = isDark
      ? ['#AE52EA', '#C77CFF', '#8B3FC5', '#E8D5FF', '#7932AE']  // violet family + pale highlight
      : ['#C9A227', '#D4AF37', '#B8891F', '#E4C55C', '#9E7618']; // champagne gold family
    const layerBase = w > 1280 ? 220 : 130;
    const layers = [
      // { drift-vx, drift-vy, radius-scale, alpha-max, count }
      { vx: -0.020, vy:  0.030, rMin: 0.30, rMax: 0.75, alpha: isDark ? 0.55 : 0.32, count: Math.floor(layerBase * 0.55) },
      { vx: -0.055, vy:  0.070, rMin: 0.55, rMax: 1.15, alpha: isDark ? 0.72 : 0.50, count: Math.floor(layerBase * 0.30) },
      { vx: -0.110, vy:  0.130, rMin: 0.90, rMax: 1.75, alpha: isDark ? 0.92 : 0.75, count: Math.floor(layerBase * 0.15) },
    ];
    const stars = [];
    layers.forEach((L, li) => {
      for (let i = 0; i < L.count; i++) {
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: L.rMin + Math.random() * (L.rMax - L.rMin),
          vx: L.vx,
          vy: L.vy,
          alpha: L.alpha,
          layer: li,
          tint: starTints[Math.floor(Math.random() * starTints.length)],
          phase: Math.random() * Math.PI * 2,
          pulseSpeed: 0.006 + Math.random() * 0.014,
        });
      }
    });

    const drawStarfield = () => {
      if (paused) { animRef.current = requestAnimationFrame(drawStarfield); return; }
      ctx.clearRect(0, 0, w, h);
      stars.forEach((s) => {
        s.x += s.vx; s.y += s.vy; s.phase += s.pulseSpeed;
        // wrap
        if (s.x < -4) s.x = w + 4;
        if (s.x > w + 4) s.x = -4;
        if (s.y > h + 4) { s.y = -4; s.x = Math.random() * w; }
        if (s.y < -4) s.y = h + 4;
        const twinkle = 0.65 + 0.35 * Math.sin(s.phase);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * (0.85 + 0.30 * twinkle), 0, Math.PI * 2);
        ctx.fillStyle = s.tint;
        ctx.globalAlpha = s.alpha * twinkle;
        ctx.fill();
        // halo on the largest (nearest) layer only — stronger in dark
        if (s.layer === 2 && twinkle > 0.85) {
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * (isDark ? 3.4 : 2.6), 0, Math.PI * 2);
          ctx.fillStyle = s.tint;
          ctx.globalAlpha = s.alpha * (isDark ? 0.18 : 0.12) * twinkle;
          ctx.fill();
        }
      });
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(drawStarfield);
    };
    drawStarfield();

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', setSize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      data-testid={testId}
      aria-hidden="true"
    />
  );
}
